import {TestIdProps} from 'shared/web';

import {ErrorStatus} from '../../ErrorStatus/ErrorStatus';
import {useTranslationContext} from '../../TranslationProvider/TranslationContext';

interface ThrownErrorStatusProps extends TestIdProps {
  error: Error;
  onRetry: () => void;
}

/**
 * Renders the error caught by `DataStatus`, with an action to retry rendering the children.
 *
 * The headline is deliberately left to `ErrorStatus`, which falls back to a translated, generic
 * message. Showing `error.name` there would put an untranslated `TypeError` in front of the user.
 * The raw message stays as the subheadline, mirroring `ErrorPage`, so a screenshot from a user
 * still identifies the failure - the same detail is also sent to Sentry and to the in-app bug
 * report by `DataStatus.componentDidCatch`.
 *
 * Split out so the retry label can be translated: `DataStatus` has to be a class component to act
 * as an error boundary, so it cannot call `useTranslationContext` itself.
 */
export function ThrownErrorStatus(props: ThrownErrorStatusProps) {
  const t = useTranslationContext();

  return (
    <ErrorStatus
      subheadline={props.error.message}
      action={{title: t('general.actions.retry'), onClick: props.onRetry}}
      data-testid={props['data-testid']}
    />
  );
}
